import { log as logAutoApplySetupButtonClickEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupButtonClickEvent';
import { log as logAutoApplySetupPageViewEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupPageViewEvent';
import { log as logAutoApplySetupErrorEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupErrorEvent';
import { log as logAutoApplySetupResumeConfirmationEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupResumeConfirmationEvent';
import { log as logAutoApplySetupPageStayedEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupPageStayedEvent';
import { log as logAutoApplyReviewPageViewEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewPageViewEvent';
import { log as logAutoApplyReviewPageStayedEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewPageStayedEvent';
import { log as logAutoApplyReviewButtonClickEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import { log as logAutoApplyReviewTabSwitchEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewTabSwitchEvent';
import { log as logAutoApplyReviewSubmitEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewSubmitEvent';
import { log as logAutoApplyReviewErrorEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewErrorEvent';
import { log as logAutoApplyReviewDraftActionEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewDraftActionEvent';
import { log as logAutoApplyReviewFieldEditEvent } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewFieldEditEvent';
import { log as logIaQuestionInteraction } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/IaQuestionInteraction';

export type EventLogger = {
    /**
     *
     * Always log this immediately before any validation or operation to represent a true FE user event
     */
    logAutoApplySetupButtonClickEvent?: (
        arg: Omit<Parameters<typeof logAutoApplySetupButtonClickEvent>[0], 'setupLogUid'>,
    ) => void;
    logAutoApplySetupPageViewEvent?: (
        arg: Omit<Parameters<typeof logAutoApplySetupPageViewEvent>[0], 'setupLogUid'>,
    ) => void;
    logAutoApplySetupErrorEvent?: (
        arg: Omit<Parameters<typeof logAutoApplySetupErrorEvent>[0], 'setupLogUid'>,
    ) => void;
    logAutoApplySetupPageStayedEvent?: (
        arg: Omit<Parameters<typeof logAutoApplySetupPageStayedEvent>[0], 'setupLogUid'>,
    ) => void;
    logAutoApplySetupResumeConfirmationEvent?: (
        arg: Omit<Parameters<typeof logAutoApplySetupResumeConfirmationEvent>[0], 'setupLogUid'>,
    ) => void;
    logReviewPageView?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewPageViewEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewPageStayed?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewPageStayedEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewButtonClick?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewButtonClickEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewTabSwitch?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewTabSwitchEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewSubmit?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewSubmitEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewError?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewErrorEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewDraftAction?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewDraftActionEvent>[0], 'reviewLogUid'>,
    ) => void;
    logReviewFieldEdit?: (
        arg: Omit<Parameters<typeof logAutoApplyReviewFieldEditEvent>[0], 'reviewLogUid'>,
    ) => void;
    /**
     * Per-question jspl field telemetry (view/answer/error/time-spent), read via
     * `useFieldTelemetryData` and flushed after save or on page abandonment. Unlike the other events
     * here, `IaQuestionInteraction` carries no log uid of its own — join to a session by `iaUid` and
     * `applicationDraftId` instead — so this is always present, not gated on setupLogUid/reviewLogUid.
     *
     * Returns whether the row was accepted (the transport didn't throw), unlike every other method
     * here. The caller retries a row that comes back `false` on its next flush rather than treating
     * it as logged — `markAsLogged()` would otherwise permanently drop it on a transient failure.
     */
    logQuestionInteraction?: (arg: Parameters<typeof logIaQuestionInteraction>[0]) => boolean;

    /**
     *
     * @returns the current url pathname in both SSR and CSR but not reactive
     */
    getPathname: () => string;
};

/**
 * Telemetry must never change product behaviour. Several of these are called from inside a `catch`
 * that is already reporting a failure, so a throw here would be re-caught by the enclosing product
 * handler and invert the outcome — a persisted save would report a save error and drop the draft.
 * There is no better channel to report a broken transport on, so the throw is dropped.
 */
const safeLog = <A>(log: (arg: A) => void, arg: A): void => {
    try {
        log(arg);
    } catch {
        return;
    }
};

/** Like `safeLog`, but reports success/failure instead of swallowing it — see `logQuestionInteraction`. */
const safeLogWithResult = <A>(log: (arg: A) => void, arg: A): boolean => {
    try {
        log(arg);
        return true;
    } catch {
        return false;
    }
};

export const createClientEventLogger = (
    setupLogUid?: string,
    reviewLogUid?: string,
): EventLogger => {
    return {
        ...(setupLogUid
            ? {
                  logAutoApplySetupButtonClickEvent: (arg) => {
                      safeLog(logAutoApplySetupButtonClickEvent, { ...arg, setupLogUid });
                  },
                  logAutoApplySetupPageViewEvent: (arg) => {
                      safeLog(logAutoApplySetupPageViewEvent, { ...arg, setupLogUid });
                  },
                  logAutoApplySetupPageStayedEvent: (arg) => {
                      safeLog(logAutoApplySetupPageStayedEvent, { ...arg, setupLogUid });
                  },
                  logAutoApplySetupErrorEvent: (arg) => {
                      safeLog(logAutoApplySetupErrorEvent, { ...arg, setupLogUid });
                  },
                  logAutoApplySetupResumeConfirmationEvent: (arg) => {
                      safeLog(logAutoApplySetupResumeConfirmationEvent, { ...arg, setupLogUid });
                  },
              }
            : {}),
        ...(reviewLogUid
            ? {
                  logReviewPageView: (arg) => {
                      safeLog(logAutoApplyReviewPageViewEvent, { ...arg, reviewLogUid });
                  },
                  logReviewPageStayed: (arg) => {
                      safeLog(logAutoApplyReviewPageStayedEvent, { ...arg, reviewLogUid });
                  },
                  logReviewButtonClick: (arg) => {
                      safeLog(logAutoApplyReviewButtonClickEvent, { ...arg, reviewLogUid });
                  },
                  logReviewTabSwitch: (arg) => {
                      safeLog(logAutoApplyReviewTabSwitchEvent, { ...arg, reviewLogUid });
                  },
                  logReviewSubmit: (arg) => {
                      safeLog(logAutoApplyReviewSubmitEvent, { ...arg, reviewLogUid });
                  },
                  logReviewError: (arg) => {
                      safeLog(logAutoApplyReviewErrorEvent, { ...arg, reviewLogUid });
                  },
                  logReviewDraftAction: (arg) => {
                      safeLog(logAutoApplyReviewDraftActionEvent, { ...arg, reviewLogUid });
                  },
                  logReviewFieldEdit: (arg) => {
                      safeLog(logAutoApplyReviewFieldEditEvent, { ...arg, reviewLogUid });
                  },
              }
            : {}),

        logQuestionInteraction: (arg) => safeLogWithResult(logIaQuestionInteraction, arg),

        getPathname: () => globalThis.location.pathname,
    };
};
